import React, { Component, Fragment } from 'react';
import { inject, observer } from 'mobx-react';
import { when } from 'mobx';
import { withRouter } from 'react-router-dom';
import { useTable, useFilters, useGlobalFilter } from 'react-table';
import Select from 'react-select';
import InventoryHeader from './InventoryHeader';
import Loading from '../Loading';
import { getStockStatusLabel } from './stockStatus';

const flattenTableDataWithCategories = (skusByCategoryForLiveInventory) => {
    let flattenedData = [];
    for (let category in skusByCategoryForLiveInventory) {
        flattenedData.push({ isCategory: true, category });
        if (skusByCategoryForLiveInventory[category]) {
            skusByCategoryForLiveInventory[category].forEach(item => {
                flattenedData.push({ ...item, category });
            });
        }
    }
    return flattenedData;
};

const MultiSelectFilter = ({ column: { id }, filters, setFilters, preFilteredRows }) => {
    const currentValue = filters.find(f => f.id === id)?.value || [];
    const options = React.useMemo(() => {
        const options = new Set();
        preFilteredRows.forEach(row => {
            if (!row.original.isCategory) {
                options.add(row.values[id]);
            }
        });
        return [...options].map(option => ({ value: option, label: option }));
    }, [id, preFilteredRows]);

    const colourStyles = {
        option: () => {
          return {
            color: 'black',
            paddingTop: '10px'
          };
        },
      };

    return (
        <div style={{minWidth: '100px', maxWidth: '90%', margin: 'auto'}}>
            <Select
                isMulti
                value={currentValue.map(val => ({ value: val, label: val }))}
                options={options}
                onChange={selected => {
                    const value = selected ? selected.map(option => option.value) : [];
                    setFilters(prev => {
                    const rest = prev.filter(f => f.id !== id);
                    return value.length ? [...rest, { id, value }] : rest;
                    });
                }}
                placeholder="Select items..."
                styles={colourStyles}
            />
        </div>
    );
};

const BooleanSelectFilter = ({ column: { id }, filters, setFilters }) => {
    const currentValue = filters.find(f => f.id === id)?.value || '';
    return (
        <select
            value={currentValue}
            onChange={e => {
            const val = e.target.value || undefined;
            setFilters(prev => {
                const rest = prev.filter(f => f.id !== id);
                return val ? [...rest, { id, value: val }] : rest;
            });
            }}
        >
            <option value="">All</option>
            <option value="true">Yes</option>
            <option value="false">No</option>
        </select>
    );
};

const NumberRangeColumnFilter = ({ column: { id }, filters, setFilters }) => {
    const currentValue = filters.find(f => f.id === id)?.value || [undefined, undefined];
    const [minValue, setMinValue] = React.useState(currentValue[0]);
    const [maxValue, setMaxValue] = React.useState(currentValue[1]);

    const applyFilter = (min, max) => {
        setFilters(prev => {
            const rest = prev.filter(f => f.id !== id);
            return (min !== undefined || max !== undefined)
                ? [...rest, { id, value: [min, max] }]
                : rest;
        });
    };

    const handleBlur = () => {
        applyFilter(minValue, maxValue);
    };

    return (
        <div style={{ display: 'flex', justifyContent: 'center', marginTop: '10px' }}>
            <input
                value={minValue ?? ''}
                type="number"
                onChange={(e) => {
                    const val = e.target.value;
                    setMinValue(val ? parseInt(val, 10) : undefined);
                }}
                onBlur={handleBlur}
                placeholder={`Min`}
                style={{
                    width: '70px',
                    marginRight: '5px',
                    marginLeft: '5px'
                }}
            />
            to
            <input
                value={maxValue ?? ''}
                type="number"
                onChange={(e) => {
                    const val = e.target.value;
                    setMaxValue(val ? parseInt(val, 10) : undefined);
                }}
                onBlur={handleBlur}
                placeholder={`Max`}
                style={{
                    width: '70px',
                    marginRight: '5px',
                    marginLeft: '5px'
                }}
            />
        </div>
    );
};

const numberRangeFilterFn = (rows, id, filterValue) => {
    const [min, max] = filterValue;
    return rows.filter(row => {
        const rowValue = row.values[id];
        return (
            (min === undefined || rowValue >= min) &&
            (max === undefined || rowValue <= max)
        );
    });
};

numberRangeFilterFn.autoRemove = val => !val.length;

@inject('InventoryStore')
@observer
class LiveInventory extends Component {
    constructor(props) {
        super(props);
        this.categoryRef = [];
    }

    componentDidMount = () => {
        const {
            InventoryStore: { handleGetLiveInventoryBatches },
        } = this.props;

        document.body.classList.add('current-stock-dashboard-scroll');
        handleGetLiveInventoryBatches();
        when(() => this.props.InventoryStore.totalSkus > 0);
        this.interval = setInterval(() => {
            handleGetLiveInventoryBatches();
            when(() => this.props.InventoryStore.loading === false);
        }, 1000 * 60 * 1); // 60 seconds
    };

    componentWillUnmount() {
        document.body.classList.remove('current-stock-dashboard-scroll');
        clearInterval(this.interval);
    }

    render() {
        const {
            InventoryStore: { skusByCategoryForLiveInventory },
        } = this.props;

        if (!skusByCategoryForLiveInventory) {
            return <Loading />;
        }

        const flattenedData = flattenTableDataWithCategories(skusByCategoryForLiveInventory);

        return (
            <Fragment>
                <div className="current-stock-dashboard-page" style={{ maxWidth: '100%' }}>
                    <InventoryHeader title="Current Stock Dashboard" />
                    <div className="current-stock-dashboard current-stock-dashboard--meal">
                        <TableComponent data={flattenedData} />
                    </div>
                </div>
            </Fragment>
        );
    }
}

const TableComponent = ({ data }) => {
    // Setting initial state
    const [filters, setFilters] = React.useState([
        {
            id: 'eligible_outlet',
            value: 'true',
        },
        {
            id: 'price_table_on',
            value: 'true',
        }
    ]);

    const columns = React.useMemo(
        () => [
            {
                Header: 'Items',
                accessor: 'name',
                Filter: (props) => <MultiSelectFilter {...props} filters={filters} setFilters={setFilters} />,
                filter: 'includesSome',
            },
            {
                Header: 'AVAILABLE FOR SALE?',
                accessor: 'available_for_sale',
                Cell: ({ value }) => (<img
                    src={value ? "https://image.popcontent.link/green_checkmark.svg" : "https://image.popcontent.link/red_cross.svg"}
                    alt={value ? 'Yes' : 'No'}
                    style={{ width: '20px', height: '20px' }}
                />),
                Filter: (props) => <BooleanSelectFilter {...props} filters={filters} setFilters={setFilters} />,
            },
            {
                Header: 'UNSOLD STOCK',
                accessor: 'quantity_left',
                Cell: ({ value }) => getStockStatusLabel(value),
                Filter: (props) => <NumberRangeColumnFilter {...props} filters={filters} setFilters={setFilters} />,
                filter: 'between',
            },
            {
                Header: 'PRE-ORDERS STOCK',
                accessor: 'preorders',
                Filter: (props) => <NumberRangeColumnFilter {...props} filters={filters} setFilters={setFilters} />,
                filter: 'between',
            },
            {
                Header: 'STOCK IN OUTLET',
                accessor: 'virtual_stock',
                Cell: ({ value }) => getStockStatusLabel(value),
                Filter: (props) => <NumberRangeColumnFilter {...props} filters={filters} setFilters={setFilters} />,
                filter: 'between',
            },
            {
                Header: 'NOT ON HOLD?',
                accessor: 'not_on_hold',
                Cell: ({ value }) => (<img
                    src={value ? "https://image.popcontent.link/green_checkmark.svg" : "https://image.popcontent.link/red_cross.svg"}
                    alt={value ? 'Yes' : 'No'}
                    style={{ width: '20px', height: '20px' }}
                />),
                Filter: (props) => <BooleanSelectFilter {...props} filters={filters} setFilters={setFilters} />,
            },
            {
                Header: 'COMPONENT IN STOCK?',
                accessor: 'in_stock',
                Cell: ({ value }) => (<img
                    src={value ? "https://image.popcontent.link/green_checkmark.svg" : "https://image.popcontent.link/red_cross.svg"}
                    alt={value ? 'Yes' : 'No'}
                    style={{ width: '20px', height: '20px' }}
                />),
                Filter: (props) => <BooleanSelectFilter {...props} filters={filters} setFilters={setFilters} />,
            },
            {
                Header: 'IN DAILY MENU?',
                accessor: 'in_daily_menu',
                Cell: ({ value }) => (<img
                    src={value ? "https://image.popcontent.link/green_checkmark.svg" : "https://image.popcontent.link/red_cross.svg"}
                    alt={value ? 'Yes' : 'No'}
                    style={{ width: '20px', height: '20px' }}
                />),
                Filter: (props) => <BooleanSelectFilter {...props} filters={filters} setFilters={setFilters} />,
            },
            {
                Header: 'PRICE TABLE ON?',
                accessor: 'price_table_on',
                Cell: ({ value }) => (<img
                    src={value ? "https://image.popcontent.link/green_checkmark.svg" : "https://image.popcontent.link/red_cross.svg"}
                    alt={value ? 'Yes' : 'No'}
                    style={{ width: '20px', height: '20px' }}
                />),
                Filter: (props) => <BooleanSelectFilter {...props} filters={filters} setFilters={setFilters} />,
            },
            {
                Header: 'ELIGIBLE OUTLET?',
                accessor: 'eligible_outlet',
                Cell: ({ value }) => (<img
                    src={value ? "https://image.popcontent.link/green_checkmark.svg" : "https://image.popcontent.link/red_cross.svg"}
                    alt={value ? 'Yes' : 'No'}
                    style={{ width: '20px', height: '20px' }}
                />),
                Filter: (props) => <BooleanSelectFilter {...props} filters={filters} setFilters={setFilters} />,
            },
            {
                Header: 'MANDATORY COMBO OPTION AVAILABLE?',
                accessor: 'mandatory_combo_option_available',
                Cell: ({ value }) => (<img
                    src={value ? "https://image.popcontent.link/green_checkmark.svg" : "https://image.popcontent.link/red_cross.svg"}
                    alt={value ? 'Yes' : 'No'}
                    style={{ width: '20px', height: '20px' }}
                />),
                Filter: (props) => <BooleanSelectFilter {...props} filters={filters} setFilters={setFilters} />,
            }
        ],
        [filters, setFilters]
    );

    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        rows,
        prepareRow,    
        setAllFilters
    } = useTable(
        {
            columns,
            data,
            defaultColumn: { Filter: () => null },
            filterTypes: {
                between: numberRangeFilterFn,
                includesSome: (rows, id, filterValue) => {
                    if (filterValue.length === 0) return rows;
                    return rows.filter(row => filterValue.includes(row.values[id]));
                },
            },
        },
        useFilters,
        useGlobalFilter 
    );

    React.useEffect(() => {
        if (data.length > 0) {
            setAllFilters(filters); // Sync only after data loads
        }
    }, [data, filters]); 

    return (
        <Fragment>
            <table {...getTableProps()} style={{ width: '100%', fontSize: '15px' }}  className="-striped current-stock-dashboard__table">
                <thead style={{top: '48px', position: 'sticky', backgroundColor: '#303234', color: 'white', 
                        zIndex: '100', textAlign: 'center', paddingTop: '10px', paddingBottom: '10px'}}>
                    {headerGroups.map(headerGroup => (
                        <tr {...headerGroup.getHeaderGroupProps()}>
                            {headerGroup.headers.map(column => (
                                <th {...column.getHeaderProps()} style={{padding: '10px 0'}}>
                                    {column.render('Header')}
                                    <div>{column.canFilter ? column.render('Filter') : null}</div>
                                </th>
                            ))}
                        </tr>
                    ))}
                </thead>
                <tbody {...getTableBodyProps()}>
                    {rows.map(row => {
                        prepareRow(row);
                        return row.original.isCategory ? (
                            <tr {...row.getRowProps()} style={{backgroundColor: 'rgba(246,185,78,255)', paddingTop: '15px', 
                            paddingBottom: '15px', paddingLeft: '30px', color: 'black', textTransform: 'uppercase', textAlign: "left"}}>
                                <td colSpan={columns.length} style={{padding: '7.5px'}}>
                                    <strong>{row.original.category}</strong>
                                </td>
                            </tr>
                        ) : (
                            <tr {...row.getRowProps()}>
                                {row.cells.map(cell => {
                                    return <td {...cell.getCellProps()} style={{paddingTop: '7.5px'}}>{cell.render('Cell')}</td>;
                                })}
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </Fragment>
    );
};

export default withRouter(LiveInventory);
